import type { HttpClient } from '../client/http';
import type { ListResponse } from '../client/types';
import type { ListOpts } from './collection';

export interface PublicCatalogMethods {
  /**
   * Lista shared docs públicos (`is_public=true` em `app_shared_collections`).
   *
   * Funciona pré-auth — não precisa de token. Resposta é cache CDN
   * agressiva (~5min TTL). Apps que precisam fresh data sempre devem
   * usar `hook.db.shared(name).list()` no path autenticado.
   *
   * @throws SdkError em network/5xx (retried 3x).
   * @throws SdkValidationError em 400 (limit > 100, etc).
   */
  list(opts?: ListOpts): Promise<ListResponse>;
}

/**
 * P-CAT-PUB — Cliente de shared catalog público (sem auth).
 *
 * Uso típico em pre-auth screens (wizards de onboarding, telas de marketing
 * que mostram catálogo antes do signup):
 *
 * ```ts
 * const { docs } = await hook.publicCatalog('ingredients').list({ limit: 100 });
 * ```
 *
 * Backend gateia por (registered AND is_public=true) — apps que quiserem
 * expor uma collection devem registrar via app_shared_collections + flag
 * is_public=true. Default é fechado.
 */
export function createPublicCatalog(client: HttpClient, appId: string) {
  return function publicCatalog(name: string): PublicCatalogMethods {
    const base = `/api/public/app/${appId}/db/${name}/shared`;
    return {
      list(opts: ListOpts = {}): Promise<ListResponse> {
        const query: Record<string, string | number | undefined> = {
          limit: opts.limit ?? 50,
          orderBy: opts.orderBy ?? 'created_at',
          order: opts.order ?? 'desc',
        };
        if (opts.cursor) query.cursor = opts.cursor;
        return client.request<ListResponse>('GET', base, {
          query,
          skipAuth: true,
        });
      },
    };
  };
}
